import { IconButton, Tooltip, useTheme } from '@mui/material';
import { pdf } from '@react-pdf/renderer';
import { Printer } from 'iconsax-react';
import { type ReactElement, useState } from 'react';

import { DEFAULT_COLUMN_HEADER_FORMATTER } from '../../../../pdf/components/PdfTable';
import { DataTableListPdf } from '../../../../pdf/DataTableListPdf';
import type { Column } from '../../TableDataModel';
import type { ReadableSet } from '../../useApiTableData';

type PrintButtonProps<TRow extends { id: string }> = {
  renderedColumns: ReadableSet<Column<TRow>>;
  getAllRows: () => Promise<TRow[]>;
  tableName: string;
};

export const PrintButton = <TRow extends { id: string }>({
  renderedColumns,
  getAllRows,
  tableName,
}: PrintButtonProps<TRow>): ReactElement => {
  const theme = useTheme();
  const [isOpeningPdf, setIsOpeningPdf] = useState(false);

  // The span keeps the tooltip working while the button is disabled.
  return (
    <Tooltip title='Print'>
      <span>
        <IconButton
          onClick={async (): Promise<void> => {
            setIsOpeningPdf(true);
            const pdfBlob = await pdf(
              <DataTableListPdf
                columns={[...renderedColumns].map((col) => ({
                  columnHeaderToPdf: DEFAULT_COLUMN_HEADER_FORMATTER,
                  ...col,
                }))}
                rows={await getAllRows()}
                title={tableName}
              />,
            ).toBlob();
            const pdfDataUri = URL.createObjectURL(pdfBlob);
            window.open(pdfDataUri, '', '_blank');
            setIsOpeningPdf(false);
          }}
          disabled={isOpeningPdf}
        >
          <Printer variant='TwoTone' color={theme.palette.SlabIndigo['700']} />
        </IconButton>
      </span>
    </Tooltip>
  );
};
